import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function SizeGuideSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'table';

    const unitLabels = content.unit_labels || ['CM', 'Pulgadas'];
    const columns = content.columns || [];
    const rows = content.rows || [];
    const calcRanges = content.calculator_ranges || [];

    const [selectedUnit, setSelectedUnit] = useState(0);
    const [calculatorInput, setCalculatorInput] = useState('');
    const [recommendedSize, setRecommendedSize] = useState(null);

    if (columns.length === 0 || rows.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const getValues = (row) => selectedUnit === 0 ? (row.values_a || []) : (row.values_b || []);

    const findSize = () => {
        const val = parseFloat(calculatorInput);
        if (isNaN(val) || calcRanges.length === 0) { setRecommendedSize(null); return; }
        const match = calcRanges.find(r => val >= parseFloat(r.min) && val <= parseFloat(r.max));
        setRecommendedSize(match ? match.size_label : null);
    };

    // ── Unit toggle ──
    const UnitToggle = () => unitLabels.length >= 2 && (
        <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                {unitLabels.map((label, i) => (
                    <button key={i} onClick={() => setSelectedUnit(i)}
                        className="px-5 py-2 text-sm font-semibold rounded-full transition-all duration-300"
                        style={selectedUnit === i
                            ? { backgroundColor: s.accent, color: '#ffffff' }
                            : { color: s.textSub }}>
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );

    // ── Calculator ──
    const Calculator = () => calcRanges.length > 0 && (
        <div className={`${card} p-6 mt-8 max-w-md mx-auto`}>
            <h4 className="text-sm font-bold mb-3" style={{ color: s.text }}>
                {content.calculator_label || 'Encuentra tu talla'}
            </h4>
            <div className="flex gap-3">
                <input
                    type="number"
                    value={calculatorInput}
                    onChange={e => { setCalculatorInput(e.target.value); setRecommendedSize(null); }}
                    onKeyDown={e => e.key === 'Enter' && findSize()}
                    placeholder={content.calculator_placeholder || 'Ingresa tu medida'}
                    className="flex-1 px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2"
                    style={{
                        backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff',
                        borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
                        color: s.text,
                        '--tw-ring-color': s.accent,
                    }}
                />
                <button onClick={findSize}
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: s.accent }}>
                    Buscar
                </button>
            </div>
            {recommendedSize && (
                <div className="mt-4 p-3 rounded-lg text-center" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                    <span className="text-sm" style={{ color: s.textSub }}>Tu talla recomendada: </span>
                    <span className="text-lg font-bold" style={{ color: s.accent }}>{recommendedSize}</span>
                </div>
            )}
            {calculatorInput && recommendedSize === null && (
                <p className="mt-3 text-xs text-center" style={{ color: s.textSub }}>
                    Presiona "Buscar" para encontrar tu talla
                </p>
            )}
        </div>
    );

    // ── Table variant ──
    if (variant === 'table') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <UnitToggle />
                    <div className={`overflow-x-auto transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        <table className="w-full min-w-[480px]">
                            <thead>
                                <tr>
                                    <th className="px-4 py-3 text-sm font-bold text-left" style={{ color: s.text, borderBottom: `2px solid ${s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb'}` }}>
                                        Talla
                                    </th>
                                    {columns.map((col, i) => (
                                        <th key={i} className="px-4 py-3 text-sm font-bold text-center"
                                            style={{ color: s.text, borderBottom: `2px solid ${s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb'}` }}>
                                            {col.label || col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => {
                                    const isRecommended = recommendedSize && row.size_label === recommendedSize;
                                    const values = getValues(row);
                                    return (
                                        <tr key={ri}
                                            style={{
                                                backgroundColor: isRecommended
                                                    ? hexToRgba(s.accent, 0.12)
                                                    : ri % 2 === 0
                                                        ? (s.dark ? 'rgba(255,255,255,0.03)' : '#f9fafb')
                                                        : undefined,
                                                transition: 'background-color 0.3s',
                                            }}>
                                            <td className="px-4 py-3 text-sm font-semibold" style={{
                                                color: isRecommended ? s.accent : s.text,
                                                borderBottom: s.dark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f3f4f6',
                                            }}>
                                                {row.size_label}
                                                {isRecommended && (
                                                    <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                                                        style={{ backgroundColor: s.accent }}>
                                                        Recomendada
                                                    </span>
                                                )}
                                            </td>
                                            {values.map((val, ci) => (
                                                <td key={ci} className="px-4 py-3 text-sm text-center"
                                                    style={{ color: s.textSub, borderBottom: s.dark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f3f4f6' }}>
                                                    {val}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Calculator />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Visual variant (cards) ──
    if (variant === 'visual') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <UnitToggle />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {rows.map((row, ri) => {
                            const isRecommended = recommendedSize && row.size_label === recommendedSize;
                            const values = getValues(row);
                            return (
                                <div key={ri}
                                    className={`${card} p-5 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{
                                        transitionDelay: visible ? `${ri * 80}ms` : '0ms',
                                        borderColor: isRecommended ? s.accent : undefined,
                                        borderWidth: isRecommended ? '2px' : undefined,
                                    }}>
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-xl font-bold" style={{ color: isRecommended ? s.accent : s.text }}>
                                            {row.size_label}
                                        </span>
                                        {isRecommended && (
                                            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white"
                                                style={{ backgroundColor: s.accent }}>
                                                Recomendada
                                            </span>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        {columns.map((col, ci) => (
                                            <div key={ci} className="flex justify-between items-center text-sm">
                                                <span style={{ color: s.textSub }}>{col.label || col}</span>
                                                <span className="font-medium" style={{ color: s.text }}>{values[ci] || '-'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Calculator />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Accordion variant ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <UnitToggle />
                <AccordionRows rows={rows} columns={columns} getValues={getValues} recommendedSize={recommendedSize} s={s} card={card} visible={visible} />
                <Calculator />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}

function AccordionRows({ rows, columns, getValues, recommendedSize, s, card, visible }) {
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <div className="max-w-2xl mx-auto space-y-3">
            {rows.map((row, ri) => {
                const isOpen = openIndex === ri;
                const isRecommended = recommendedSize && row.size_label === recommendedSize;
                const values = getValues(row);
                return (
                    <div key={ri}
                        className={`${card} overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        style={{
                            transitionDelay: visible ? `${ri * 60}ms` : '0ms',
                            borderColor: isRecommended ? s.accent : undefined,
                            borderWidth: isRecommended ? '2px' : undefined,
                        }}>
                        <button
                            onClick={() => setOpenIndex(isOpen ? null : ri)}
                            className="w-full px-5 py-4 flex items-center justify-between text-left">
                            <div className="flex items-center gap-3">
                                <span className="text-base font-bold" style={{ color: isRecommended ? s.accent : s.text }}>
                                    {row.size_label}
                                </span>
                                {isRecommended && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                                        style={{ backgroundColor: s.accent }}>
                                        Recomendada
                                    </span>
                                )}
                            </div>
                            <svg className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-5 pb-4 space-y-2" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
                                <div className="pt-3">
                                    {columns.map((col, ci) => (
                                        <div key={ci} className="flex justify-between items-center py-1.5 text-sm">
                                            <span style={{ color: s.textSub }}>{col.label || col}</span>
                                            <span className="font-medium" style={{ color: s.text }}>{values[ci] || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
